/**
 * operatorActions.ts
 *
 * The orb's quick actions and free-text ask, each wired to a REAL backend.
 * Nothing here is a stub. Where a backend genuinely cannot run (no subject,
 * no org), the caller returns an honest no-action message rather than a fake
 * success. No em-dashes in operator-facing strings (Rule 7).
 *
 * Wired backends:
 *   ask           -> orbi-operator-ask edge fn  (grounded, read-only, cited)
 *   network       -> client_building_health REST (read-only, known shape)
 *   insights      -> orbi-operator-ask          (reads Patterns + Insights memory)
 *   ask-to-call   -> orbi-operator-call edge fn  (armed, known-subscriber bounded)
 *
 * Auth: every read here is RLS-gated, so it runs as the OPERATOR or it does not
 * run. There is no anon fallback. See the auth block further down for why the
 * old one was worse than no answer at all.
 */

import type { PlatformContext } from './platformContext';
import {
  resolveOperatorAuthHeaders,
  operatorAuthMessage,
  isAuthFailureStatus,
  rejectedSessionMessage,
  authRefusalMessage,
  type OperatorAuthFailure,
  type OperatorTokenProvider,
} from './operatorSession';

const __ORBI_ENV__: any =
  (typeof window !== 'undefined' && (window as any).__ORBI_ENV__) || {};
/* DROP-IN (Phase 3 binding 4): no hardcoded project fallback - a dock
   dropped into another vertical must never silently point at Teglo. Every
   page (Teglo's included) publishes SUPABASE_URL via __ORBI_ENV__; an empty
   value here fails loudly in the console instead of quietly cross-tenant. */
const SUPABASE_URL: string = __ORBI_ENV__.SUPABASE_URL || '';
if (!SUPABASE_URL && typeof console !== 'undefined') console.warn('[assistant] __ORBI_ENV__.SUPABASE_URL is not set; operator reads will fail');
const SUPABASE_ANON_KEY: string =
  __ORBI_ENV__.SUPABASE_ANON_KEY || '';
/* The pilot vertical's id. Used for exactly one comparison (networkCheck's
   operator-altitude estate pin) - never as a fallback identity. */
const TEGLO_VERTICAL_ID = 'f0000001-0000-0000-0000-000000000000';

export interface AskCitation {
  layer: string;
  table: string;
  id: string;
  label: string;
  is_test: boolean;
}

/** A grounded subscriber card the panel renders inline (heading/metric/badges/cta),
 *  so Orbi shows the subject instead of telling the operator to open the page. */
export interface AskCard {
  type: string;
  payload: {
    heading?: string;
    metric?: string;
    description?: string;
    badges?: Array<{ text: string; variant?: string }>;
    cta?: { label: string; action: string };
  };
}

export interface AskResult {
  ok: boolean;
  answer: string;
  citations: AskCitation[];
  /* Model-suggested navigation carries client_slug + section; JARVIS J1
     classifier navigation carries a config-resolved route (+ context). */
  /** `auto` distinguishes a navigation that IS the answer (the operator asked to
   *  be shown something: the panel moves the screen) from one that is an OFFER
   *  (the question belongs to another page: the operator taps, or does not).
   *  Absent means auto, which is what every path before D7a meant. */
  navigate: { client_slug?: string; section?: string; route?: string; context?: Record<string, unknown>; auto?: boolean } | null;
  card?: AskCard | null;
  /** Generative UI blocks (Phase 1). Raw server payload; the caller MUST pass it
   *  through validateUiBlocks (uiRegistry) before anything renders. */
  ui?: unknown;
  /** The subscriber the answer resolved to (audit A1): lets the dock prime its
   *  voice subject from a typed resolution, one subject store for both paths. */
  subject?: { name?: string | null; account_number?: string | null; account_id?: string | null } | null;
  used_model?: string;
  /** True when ok:false is an AUTH state (signed out / expired), whose answer
   *  is the operator-facing sentence to say verbatim - the voice nav tool used
   *  to flatten this into a generic "navigation service is unavailable", which
   *  is what the operator heard as "the system isn't allowing me" while the
   *  auth-free typed path navigated fine (2026-08-26). */
  auth_failure?: boolean;
}

export interface CustomerRef {
  account_id?: string;
  account_number?: string;
  phone?: string;
  email?: string;
}

export interface CallCandidate {
  account_id?: string;
  name: string;
  account_number: string | null;
  phone: string | null;
  status?: string | null;
  building_slug?: string | null;
  is_test?: boolean;
}

function cleanCallTarget(raw: string): string | null {
  let s = raw.trim().replace(/[?.!,]+$/, '').replace(/\b(please|now|for me|right now)\b/gi, '').trim();
  s = s.replace(/^(the|a|my|our|mr|mrs|ms|dr)\s+/i, '').trim();
  if (!s) return null;
  // Reject targets that would be ordinary requests rather than a person to dial.
  if (/^(back|again|it|them|him|her|someone|a human|support|the team|tech|the office|volume|log|logs|history)\b/i.test(s)) return null;
  if (s.length < 2 || s.length > 60) return null;
  return s;
}

export interface CallIntent {
  name: string;
  /** The operator's stated objective ("resolve her overdue invoice"), threaded
   *  into the outbound call so Orbi opens knowing what to fix. */
  reason?: string;
}

/** Split "<name> about/to resolve <reason>" into the name and the objective. */
function splitNameReason(raw: string): CallIntent | null {
  const s = raw.trim();
  const m = s.match(/^(.*?)\s+(?:about|regarding|concerning|re|because of|to\s+(?:resolve|discuss|sort(?:\s+out)?|talk(?:\s+about)?|chat(?:\s+about)?|go\s+over|check|handle|fix|deal\s+with|follow\s+up\s+on))\s*(.*)$/i);
  if (m) {
    const nm = cleanCallTarget(m[1]);
    if (!nm) return null;
    const reason = (m[2] || '').trim().replace(/[?.!]+$/, '');
    return reason ? { name: nm, reason } : { name: nm };
  }
  const nm = cleanCallTarget(s);
  return nm ? { name: nm } : null;
}

/** Detect a "call <name>" instruction in free text and return the target name plus
 *  an optional reason, or null when the text is not a dial request. Kept tight so
 *  ordinary questions ("what is the call volume") never trigger a call. */
export function detectCallIntent(text: string): CallIntent | null {
  const t = (text || '').trim();
  let m = t.match(/\bgive\s+(.+?)\s+a\s+call\b\s*(.*)$/i);
  if (m && m[1]) {
    const intent = splitNameReason(m[1]);
    if (intent && !intent.reason && m[2]) {
      const tail = m[2].replace(/^\s*(?:about|regarding|concerning|re|to)\s+/i, '').trim().replace(/[?.!]+$/, '');
      if (tail) intent.reason = tail;
    }
    return intent;
  }
  m = t.match(/\b(?:call|phone|ring|dial)\s+(?:up\s+)?(.+)$/i);
  if (m && m[1]) return splitNameReason(m[1]);
  return null;
}

/* Words that are sections or data nouns, never a person. Guards "show me the
   network" / "pull up billing" from being read as a subscriber lookup so they
   still fall through to the grounded ask. */
const NOT_A_PERSON = /\b(network|billing|status|ticket|tickets|cost|costs|discovery|dashboard|settings|customer|customers|subscribers?|appointment|appointments|map|memory|insight|insights|pattern|patterns|invoice|invoices|screen|page|view|everything|report|reports|list|table|it|them|this|that|automation|activity|alerts?|anomal(?:y|ies)|resolve|leads?)\b/i;

/** Detect a "show me <name>" style instruction (no call) so the orb can surface a
 *  subscriber card the same way "call <name>" does, minus the dial intent. Returns
 *  null for section/data phrasings so ordinary asks are untouched. */
export function detectShowIntent(text: string): { name: string } | null {
  const t = (text || '').trim();
  const m = t.match(/\b(?:show me|show|pull up|bring up|look up|open up)\s+(.+)$/i);
  if (!m || !m[1]) return null;
  const raw = m[1].trim();
  if (NOT_A_PERSON.test(raw)) return null;
  // Digit-shaped targets ("number 55102", "account 55102", "#55102") are never
  // a person to name-search: fall through to the grounded ask, where the
  // JARVIS J1 classifier resolves them to the account and navigates.
  if (/^(?:(?:account|subscriber|number|no\.?|num)\s+)*#?\s*\d{3,10}$/i.test(raw)) return null;
  const nm = cleanCallTarget(raw);
  return nm ? { name: nm } : null;
}

/** Search known subscribers by name, scoped to the active org/client lens. Used by
 *  the orb's "call <name>" intent to resolve who to dial and to disambiguate when
 *  more than one subscriber matches. */
export async function searchSubscribersByName(ctx: PlatformContext, name: string): Promise<CallCandidate[]> {
  const tokens = name.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const orParts: string[] = [];
  for (const tok of tokens) {
    const e = encodeURIComponent(tok);
    orParts.push(`first_name.ilike.*${e}*`, `last_name.ilike.*${e}*`);
  }
  const params = new URLSearchParams();
  params.set('select', 'id,account_number,first_name,last_name,phone,status,building_slug,is_test');
  if (!ctx.scope.vertical_id) return [];
  if (ctx.scope.org_id) params.set('org_id', `eq.${ctx.scope.org_id}`);
  if (ctx.scope.client_id) params.set('client_id', `eq.${ctx.scope.client_id}`);
  params.set('limit', '12');
  // Append or=(...) manually so its commas/parens survive (PostgREST needs them raw).
  const url = `${SUPABASE_URL}/rest/v1/isp_accounts?${params.toString()}&or=(${orParts.join(',')})`;
  const res = await fetch(url, { headers: await authHeaders(ctx) });
  // A 401/403 here is a stale session, not "no such subscriber". Without this
  // the old anon path returned 200 + [] and the dock said the name was unknown.
  throwIfAuthRejected(res.status);
  if (!res.ok) throw new Error(`subscriber search returned ${res.status}`);
  const rows = await res.json().catch(() => []);
  const list: CallCandidate[] = (Array.isArray(rows) ? rows : []).map((r: any) => ({
    account_id: r.id,
    name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || (r.account_number || 'Subscriber'),
    account_number: r.account_number ?? null,
    phone: r.phone ?? null,
    status: r.status ?? null,
    building_slug: r.building_slug ?? null,
    is_test: !!r.is_test,
  }));
  // Tightest match first: rows where every token appears in the full name.
  const score = (c: CallCandidate) => (tokens.every((tk) => c.name.toLowerCase().includes(tk)) ? 0 : 1);
  list.sort((a, b) => score(a) - score(b));
  if (list.length) return list;
  // FUZZY FALLBACK (audit A2): substring match cannot absorb a one-letter
  // mis-spelling ("Erica" is not a substring of "Erika"), and speech will never
  // spell reliably. subscriber_fuzzy_match (pg_trgm RPC, claim-gated in-function,
  // anon EXECUTE revoked) returns the closest matches, which flow into the SAME
  // pick-list card the exact path uses — the operator confirms, nothing is guessed.
  if (ctx.scope.vertical_id !== TEGLO_VERTICAL_ID) return [];
  try {
    const res2 = await fetch(`${SUPABASE_URL}/rest/v1/rpc/subscriber_fuzzy_match`, {
      method: 'POST',
      // await was missing here (caught by tsc 2026-08-26): spreading the
      // PROMISE produced headers with no Authorization/apikey at all, so the
      // fuzzy RPC 401'd and this fallback silently returned [] - the A2 fuzzy
      // path never actually ran from the dock.
      headers: { ...(await authHeaders(ctx)), 'Content-Type': 'application/json' },
      body: JSON.stringify({ q: name, p_org: ctx.scope.org_id || null, p_limit: 5 }),
    });
    if (!res2.ok) return [];
    const rows2 = await res2.json().catch(() => []);
    return (Array.isArray(rows2) ? rows2 : []).map((r: any) => ({
      account_id: r.account_id,
      name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || (r.account_number || 'Subscriber'),
      account_number: r.account_number ?? null,
      phone: null,
      status: null,
      building_slug: null,
      is_test: false,
    }));
  } catch {
    return []; // fuzzy is best-effort; the honest "could not find" line still shows
  }
}

/** The FULL subject record for voice priming (Eri round 2 items 6+7): account
 *  identity + billing + plan, the live connection row, and the memory thread
 *  (trust confidence, recurring issues, risk flags). Every field is a live row
 *  read; anything unavailable stays absent, never invented. Best-effort: any
 *  single read failing leaves the rest intact. */
export interface SubjectDetail {
  account_number: string;
  name: string;
  status?: string | null;
  billing_status?: string | null;
  plan_name?: string | null;
  plan_speed_mbps?: number | null;
  monthly_rate?: number | null;
  building_slug?: string | null;
  onboarding_state?: string | null;
  is_test?: boolean;
  connection_status?: string | null;
  current_speed_mbps?: number | null;
  devices_online?: number | null;
  trust_confidence?: number | null;
  trust_lifecycle?: string | null;
  recurring_issues?: string | null;
  risk_flags?: string | null;
  open_ticket_count?: number | null;
}
/* THE BUSINESS ON A ONE-ROW READ (SPINUP-WIRE-1 H, 2026-10-17). These isp_accounts reads key on
   account_number with limit=1 and run on the caller's token; RLS "authed client scope" bounds them
   to clients the caller can read. An operator who reads several clients would get whichever row
   came first once two clients share an account number, so a scoped lens names its client too. */
export const clientScopeParam = (ctx: PlatformContext | null | undefined): string =>
  ctx?.scope?.client_id ? `&client_id=eq.${encodeURIComponent(ctx.scope.client_id)}` : '';

export async function fetchSubjectDetail(ctx: PlatformContext, account_number: string): Promise<SubjectDetail | null> {
  if (!account_number) return null;
  const acctEnc = encodeURIComponent(account_number);
  if (!ctx.scope.vertical_id) return null;
  const headers = await authHeaders(ctx);
  const get = async (path: string) => {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers });
    // An auth rejection propagates out of the whole detail read: reporting a
    // subscriber with every field absent would be the same silent lie as anon.
    throwIfAuthRejected(res.status);
    if (!res.ok) return null;
    return res.json().catch(() => null);
  };
  const acctRows = await get(`isp_accounts?account_number=eq.${acctEnc}${clientScopeParam(ctx)}&select=id,account_number,first_name,last_name,status,billing_status,plan_name,plan_speed_mbps,monthly_rate,building_slug,onboarding_state,is_test&limit=1`);
  const a = Array.isArray(acctRows) ? acctRows[0] : null;
  if (!a) return null;
  const out: SubjectDetail = {
    account_number: a.account_number,
    name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.account_number,
    status: a.status ?? null,
    billing_status: a.billing_status ?? null,
    plan_name: a.plan_name ?? null,
    plan_speed_mbps: a.plan_speed_mbps ?? null,
    monthly_rate: a.monthly_rate ?? null,
    building_slug: a.building_slug ?? null,
    onboarding_state: a.onboarding_state ?? null,
    is_test: !!a.is_test,
  };
  try {
    const conn = await get(`isp_connections?account_id=eq.${encodeURIComponent(a.id)}&select=connection_status,current_speed_mbps,devices_online&limit=1`);
    const c = Array.isArray(conn) ? conn[0] : null;
    if (c) { out.connection_status = c.connection_status ?? null; out.current_speed_mbps = c.current_speed_mbps ?? null; out.devices_online = c.devices_online ?? null; }
  } catch { /* connection read best-effort */ }
  try {
    const thr = await get(`orbi_customer_insights?account_number=eq.${acctEnc}&select=confidence,lifecycle_state,recurring_issues,risk_flags&order=updated_at.desc&limit=1`);
    const t = Array.isArray(thr) ? thr[0] : null;
    if (t) { out.trust_confidence = typeof t.confidence === 'number' ? t.confidence : null; out.trust_lifecycle = t.lifecycle_state ?? null; out.recurring_issues = t.recurring_issues ?? null; out.risk_flags = t.risk_flags ?? null; }
  } catch { /* thread read best-effort */ }
  try {
    const tk = await get(`isp_tickets?account_id=eq.${encodeURIComponent(a.id)}&status=not.in.(closed,resolved,cancelled,done)&select=id&limit=20`);
    if (Array.isArray(tk)) out.open_ticket_count = tk.length;
  } catch { /* tickets best-effort */ }
  return out;
}

/* ── Operator auth: no anon fallback, ever ───────────────────────────────
 *
 *  THE BUG THIS REPLACES. The old bearer() ended `return SUPABASE_ANON_KEY`,
 *  so four different failures collapsed into one silent downgrade: no session
 *  in storage, an entry that would not parse, an entry with no access_token,
 *  and an expired one. Anon lands at PostgREST as role "anon", and the
 *  authenticated-only RLS policies on isp_accounts answer role "anon" with 200
 *  OK and an empty array - never an error. So the dock told a signed-out or
 *  expired operator "No billing row found for this subscriber", a statement
 *  about the DATA, when the truth was a statement about the SESSION. That is
 *  also the "works in a mocked harness, resolves nothing live" report: the
 *  harness had no RLS to be silent about.
 *
 *  It compounded. The old scan only knew one storage shape, so it handed back
 *  anon even for a fully signed-in operator; and being a raw localStorage
 *  SNAPSHOT it could not refresh, so any tab older than the token's one-hour
 *  life degraded the same way with no signed-out state anywhere on screen.
 *  lib/resolve-governor.js (2026-08-15) already had to route around this exact
 *  function for the Resolve drawer; this closes it at the source instead.
 *
 *  ORA.accessToken() (components/canonical/ora-config.js) is the sanctioned
 *  reader and is preferred here: it goes through the page's single registered
 *  supabase-js client, so getSession() transparently REFRESHES a session that
 *  has expired or is about to, and it already reports null for signed out and
 *  'expired' for a session that cannot be renewed. Where it is absent (the
 *  dock's standalone build, supabase-js blocked or offline) operatorSession.ts
 *  classifies storage instead - still never anon.
 *
 *  A harness now declares its auth out loud rather than inheriting anon by
 *  accident, which is the point: anon could not read these tables anyway, so
 *  sending it only converted an honest "sign in again" into an empty list. */

/** Thrown when we cannot authenticate as the operator. `message` is already the
 *  operator-facing sentence; callers surface it verbatim rather than wrapping
 *  it in a data-shaped one. */
export class OperatorAuthError extends Error {
  readonly failure: OperatorAuthFailure;
  constructor(failure: OperatorAuthFailure, message: string) {
    super(message);
    this.name = 'OperatorAuthError';
    this.failure = failure;
  }
}

/** The operator-facing sentence when `err` is an auth failure, else null. Lets a
 *  caller (here or in OrbiDock) tell "you are signed out" apart from a genuine
 *  backend error without reaching into the class. */
export function operatorAuthFailureText(err: unknown): string | null {
  return err instanceof OperatorAuthError ? err.message : null;
}

/* P0-1 (2026-08-18): a 403 is only sometimes an expiry. `authRefusalMessage` reads the role
   the server decoded and says which account is signed in; this supplies the email for that
   sentence from the page shell when there is one. See operatorSession.authRefusalMessage. */
function signedInEmail(): string | null {
  try {
    const ora = (typeof window !== 'undefined' && (window as any).ORA) || null;
    return (ora && ora.auth && ora.auth.user && ora.auth.user.email) || null;
  } catch { return null; }
}

function oraTokenProvider(): OperatorTokenProvider | null {
  try {
    const ora = (typeof window !== 'undefined' && (window as any).ORA) || null;
    if (ora && typeof ora.accessToken === 'function') return () => ora.accessToken();
  } catch { /* no page shell on this surface; fall back to reading storage */ }
  return null;
}

/** Headers for an operator-authenticated request, or a throw that says why not.
 *  There is deliberately no third outcome. */
async function authHeaders(ctx?: PlatformContext): Promise<Record<string, string>> {
  const r = await resolveOperatorAuthHeaders(SUPABASE_ANON_KEY, { provider: oraTokenProvider() });
  // This dock builds with `strict: false`, which turns off discriminated-union
  // narrowing on the `ok` literal, so each arm is read explicitly. Upstream
  // (ora-core) is strict and narrows normally; the shared module is unchanged.
  const liveScope: any = ctx?.scope || ((typeof window !== 'undefined' && (window as any).ORA && (window as any).ORA.scope) || {});
  if (r.ok) return {
    ...(r as { headers: Record<string, string> }).headers,
    'x-ora-lens-vertical': liveScope.vertical_id || liveScope.verticalId || '',
    'x-ora-lens-clients': liveScope.client_id || liveScope.clientId || '',
  };
  const fail = r as { failure: OperatorAuthFailure; message: string };
  throw new OperatorAuthError(fail.failure, fail.message);
}

/** The server refused the token we believed was live, so storage was wrong and
 *  the session is stale. Same vocabulary as a locally detected expiry, never a
 *  status code or an empty result. */
function throwIfAuthRejected(status: number): void {
  if (isAuthFailureStatus(status)) throw new OperatorAuthError('expired', rejectedSessionMessage());
}

/* ── STARTERS (D7a, 2026-09-18) ────────────────────────────────────────
   THE PANEL ASKS FOR ITS STARTERS; IT NEVER COMPOSES THEM. Until this dispatch
   the "Show me" list was built in the dock from the workspace's nav and skills,
   which meant the assistant's opening offer was the left navigation restated as
   buttons: it told an operator what they could already see, in the platform's
   own words, on a workspace that had not been set up at all.

   The server owns it now, for the two reasons that matter: the completion state
   is a read of six tenant stores behind RLS (a browser asking those directly is
   both a new read surface and a second checklist), and the copy has to come out
   of the workspace's own vocabulary, which is the same walk every other answer
   takes. One door, one set of words.

   Fails to NOTHING. A refused or errored call returns an empty list and the
   panel simply shows no starters, which is honest. It never falls back to a
   typed-in list, because a typed-in list is exactly what this replaced. */
export interface StarterOffer {
  key: string;
  label: string;
  hint: string;
  kind: 'setup' | 'ask';
  section: string | null;
  focus: string | null;
  route: string | null;
  question: string | null;
  /** The WORKSPACE'S word for the page this offer opens ("patients", not
   *  "customers"), resolved server side from the same manifest the rail reads. */
  opens_label: string | null;
}
export interface StartersResult {
  ok: boolean;
  kind: 'setup' | 'ask';
  setup_complete: boolean;
  starters: StarterOffer[];
}

export async function askStarters(ctx: PlatformContext): Promise<StartersResult> {
  const none: StartersResult = { ok: false, kind: 'ask', setup_complete: true, starters: [] };
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/orbi-operator-ask`, {
      method: 'POST',
      headers: await authHeaders(ctx),
      body: JSON.stringify({
        ask_kind: 'starters',
        /* The server requires a question on every request; this one names what is
           being asked for rather than shipping an empty string it would refuse. */
        question: 'starters',
        scope: {
          vertical_id: ctx.scope.vertical_id,
          client_id: ctx.scope.client_id,
          org_id: ctx.scope.org_id,
          altitude: ctx.scope.altitude,
          client_slug: ctx.scope.client_slug,
          client_name: ctx.scope.client_name,
        },
        platform_context: ctx.on_screen,
        clients: ctx.clients,
      }),
    });
    if (!res.ok) return none;
    const data = await res.json().catch(() => ({}));
    if (data?.ok !== true || !Array.isArray(data.starters)) return none;
    return {
      ok: true,
      kind: data.kind === 'setup' ? 'setup' : 'ask',
      setup_complete: data.setup_complete !== false,
      starters: data.starters
        .filter((s: any) => s && typeof s.label === 'string' && s.label.trim())
        .slice(0, 3)
        .map((s: any) => ({
          key: String(s.key || s.label),
          label: String(s.label),
          hint: String(s.hint || ''),
          kind: s.kind === 'setup' ? 'setup' : 'ask',
          section: s.section ? String(s.section) : null,
          focus: s.focus ? String(s.focus) : null,
          /* Same-origin only, the same guard every navigation in this dock carries. */
          route: typeof s.route === 'string' && s.route.startsWith('/') && !s.route.startsWith('//') ? s.route : null,
          question: s.question ? String(s.question) : null,
          opens_label: s.opens_label ? String(s.opens_label) : null,
        })),
    };
  } catch {
    return none;
  }
}

/* ── Free-text ask -> orbi-operator-ask ─────────────────────────────── */
export async function askOrbi(
  question: string,
  ctx: PlatformContext,
  customer: CustomerRef | null,
  opts: { uiBlocks?: boolean; jarvisNav?: boolean; classifyOnly?: boolean; recentTurns?: Array<{ role: string; text: string }> } = {}
): Promise<AskResult> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/orbi-operator-ask`, {
      method: 'POST',
      headers: await authHeaders(ctx),
      body: JSON.stringify({
        question,
        scope: {
          vertical_id: ctx.scope.vertical_id,
          client_id: ctx.scope.client_id,
          org_id: ctx.scope.org_id,
          altitude: ctx.scope.altitude,
          // Lens names so the server SCOPE block can say which client lens the
          // console is looking through, not just hold an opaque id.
          client_slug: ctx.scope.client_slug,
          client_name: ctx.scope.client_name,
        },
        customer: customer ? { account_number: customer.account_number, phone: customer.phone } : {},
        platform_context: ctx.on_screen,
        clients: ctx.clients,
        // Generative UI opt-in (Phase 1): sent only when the client feature flag
        // is on, so the flag kills the path end to end.
        ...(opts.uiBlocks ? { ui_blocks: true } : {}),
        // JARVIS J1 voice-navigation opt-in: same kill-switch shape as
        // ui_blocks, so switching the flag off kills the path end to end.
        ...(opts.jarvisNav ? { jarvis_nav: true } : {}),
        // ONE-BRAIN convergence: the voice agent routes spoken commands through
        // this SAME deterministic classifier. classify_only stops the server at
        // the classifier boundary (no model-grounded fallthrough), so a miss
        // comes back fast and the agent answers in speech itself.
        ...(opts.classifyOnly ? { classify_only: true } : {}),
        // ONE PATH (audit A1): the recent turns of THIS conversation ride the
        // request, so a follow-up question ("and their billing?") lands on a
        // server that knows what "their" means — the same envelope shape the
        // voice call start now carries.
        ...(opts.recentTurns && opts.recentTurns.length ? { recent_turns: opts.recentTurns } : {}),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (isAuthFailureStatus(res.status)) {
      return { ok: false, answer: rejectedSessionMessage(), citations: [], navigate: null, auth_failure: true };
    }
    if (!res.ok || data?.ok === false) {
      return { ok: false, answer: data?.error ? `I could not answer that: ${data.error}` : `The ask service returned ${res.status}.`, citations: [], navigate: null };
    }
    return {
      ok: true,
      answer: data.answer || 'I could not find that in the rows on screen.',
      citations: Array.isArray(data.citations) ? data.citations : [],
      navigate: data.navigate || null,
      card: data.card || null,
      ui: data.ui ?? null,
      subject: data.subject || null,
      used_model: data.used_model,
    };
  } catch (err) {
    // An auth failure is not a service outage and its message is ours, not the
    // network's, so it is safe (and necessary) to say out loud.
    const authText = operatorAuthFailureText(err);
    if (authText) return { ok: false, answer: authText, citations: [], navigate: null, auth_failure: true };
    // DISCLOSURE GUARDRAIL: log the real error to the console only; never
    // interpolate err.message (it can carry URLs, hostnames, or stack detail)
    // into model-facing content, spoken or typed.
    console.warn('[Orbi] askOrbi failed:', err);
    return { ok: false, answer: 'I could not reach the ask service right now.', citations: [], navigate: null };
  }
}

/* ── Section watch feed (Phase 3 binding 2) ───────────────────────────────
   The SAME get-work-stream feed the bell's Work tab renders; the watch never
   recomputes it. Operator-authed like every other read; any failure returns
   null so the panel opens without a watch rather than with an invented one. */
export async function fetchWorkStream(ctx: PlatformContext): Promise<Record<string, unknown> | null> {
  /* FAIL CLOSED (operator repro 2026-08-27): the watch is a tenant-scoped
     feed. With no vertical resolved there is no tenant to scope it to, and an
     unscoped call is exactly how the pilot's work items ended up under a
     freshly spun vertical's name. No watch is the honest answer. */
  if (!ctx.scope.vertical_id) return null;
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-work-stream`, {
      method: 'POST',
      headers: await authHeaders(ctx),
      body: JSON.stringify({ verticalId: ctx.scope.vertical_id, ...(ctx.scope.client_id ? { clientId: ctx.scope.client_id } : {}) }),
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null; // includes auth failures: the watch is additive, never a nag
  }
}

export interface ActionResult {
  ok: boolean;
  text: string;
  citations: AskCitation[];
}

/* ── Network -> get-connections edge fn (verify_jwt, scoped server-side) ──
   Detail for a primed subscriber, org/client counts otherwise. */
export async function networkCheck(
  ctx: PlatformContext,
  customer: CustomerRef | null
): Promise<ActionResult> {
  try {
    const detail = !!customer?.account_id;
    /* ONE QUERY (audit A4): the estate answer comes from get-connections
       mode:'summary' — the same server-side derivation the Network page's
       header renders — never the old unscoped 'counts' (which counted every
       tenant and every provider, and printed the down bucket twice). The
       lens pins mirror the page's own: the active client slug doubles as
       isp_key, and the provider follows the lens (Sonar backs TNT, UISP
       backs the Conduct estate; operator altitude defaults to Conduct,
       exactly like the page's operator-altitude pin). */
    /* LENS PIN, FAIL CLOSED (operator repro 2026-08-27). This used to read
       `client_slug || 'conduct_connect'`: at operator altitude, and on ANY
       tenant whose client lens had not resolved, the estate answer was pinned
       to the pilot's biggest client and its provider. On the pilot that reads
       as a default; on a freshly spun vertical it is the dock reporting
       Conduct Connect's network as if it were yours.
       The pin now follows the resolved lens only. Operator altitude on the
       PILOT vertical keeps its documented Conduct default (that is the page's
       own operator-altitude pin, and it is the pilot's estate); anywhere else
       an unresolved lens returns an honest cannot-answer instead of a number
       from another tenant. */
    const pilotVertical = ctx?.scope?.vertical_id === TEGLO_VERTICAL_ID;
    const ispKey = (ctx?.scope?.client_slug as string) || (pilotVertical ? 'conduct_connect' : '');
    if (!ispKey) {
      return {
        ok: false,
        text: 'I cannot read the network for this workspace yet: no client estate is resolved for it, and I will not answer from another tenant\'s.',
        citations: [],
      };
    }
    const provider = ispKey === 'tnt_internet' ? 'sonar' : 'uisp';
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-connections`, {
      method: 'POST',
      // `await` from the bearer-token fix (#303): authHeaders() is async now and
      // throws OperatorAuthError rather than degrading to anon. The payload is
      // this branch's own: get-connections no longer has a `counts` mode - the
      // paired change in that function replaced it with `summary` + provider/isp_key.
      headers: await authHeaders(ctx),
      body: JSON.stringify(detail ? { mode: 'detail', account_id: customer!.account_id, verticalId: ctx.scope.vertical_id } : { mode: 'summary', provider, isp_key: ispKey, verticalId: ctx.scope.vertical_id }),
    });
    const data = await res.json().catch(() => ({}));
    if (isAuthFailureStatus(res.status)) return { ok: false, text: rejectedSessionMessage(), citations: [] };
    if (!res.ok || data?.error) {
      return { ok: false, text: `Network feed: ${data?.error || res.status}.`, citations: [] };
    }
    if (detail) {
      const c = data.connection;
      if (!c) return { ok: true, text: 'No live connection row for this subscriber yet.', citations: [] };
      const text = `Line: ${c.connection_status ?? 'unknown'}, ${c.current_speed_mbps ?? '?'}/${c.expected_speed_mbps ?? '?'} Mbps, signal ${c.signal_strength ?? '?'}, ${c.devices_online ?? '?'} devices online (${c.source_provider ?? 'feed'}).`;
      const cites: AskCitation[] = [{ layer: 'network', table: 'isp_connections', id: customer!.account_id!, label: `Line ${c.connection_status ?? 'unknown'}`, is_test: false }];
      return { ok: true, text, citations: cites };
    }
    /* Each bucket exactly once (the old template printed the down count twice:
       once explicitly and again inside the by_status join). These are the SAME
       figures the Network page header shows, from the same summary. */
    const text = `Network across ${data.mapped ?? 0} mapped accounts: ${data.up ?? 0} verifiably up, ${data.degraded ?? 0} degraded, ${data.down ?? 0} down, ${data.no_read ?? 0} with no reading, ${data.no_record ?? 0} with no connection record. Same figures as the Network page header.`;
    const cites: AskCitation[] = [{ layer: 'network', table: 'isp_connections', id: 'summary', label: `${data.mapped ?? 0} mapped, ${data.up ?? 0} up, ${data.down ?? 0} down`, is_test: false }];
    return { ok: true, text, citations: cites };
  } catch (err) {
    const authText = operatorAuthFailureText(err);
    if (authText) return { ok: false, text: authText, citations: [] };
    return { ok: false, text: `Could not reach the network feed (${(err as Error).message}).`, citations: [] };
  }
}

/* ── Billing -> direct read of the subscriber billing fields (read-only) ── */
export async function billingRead(
  _ctx: PlatformContext,
  customer: CustomerRef | null
): Promise<ActionResult> {
  if (!customer?.account_number) {
    return { ok: false, text: 'Prime a subscriber first and I will read their billing state.', citations: [] };
  }
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/isp_accounts?account_number=eq.${encodeURIComponent(customer.account_number)}${clientScopeParam(_ctx)}&select=billing_status,monthly_rate,plan_name,billing_day,is_test&limit=1`,
      { headers: await authHeaders(_ctx) }
    );
    if (isAuthFailureStatus(res.status)) return { ok: false, text: rejectedSessionMessage(), citations: [] };
    const rows = await res.json().catch(() => []);
    const r = Array.isArray(rows) ? rows[0] : null;
    // This is the sentence the whole fix exists for: under anon it was reached
    // by an RLS-emptied 200, so a session problem was reported as a data one.
    if (!r) return { ok: false, text: 'No billing row found for this subscriber.', citations: [] };
    const text = `Billing: ${r.billing_status ?? 'unknown'}, ${r.plan_name ?? 'plan n/a'} at ${r.monthly_rate != null ? '$' + r.monthly_rate + '/mo' : 'rate n/a'}${r.billing_day != null ? ', bills on day ' + r.billing_day : ''}.`;
    const cites: AskCitation[] = [{ layer: 'billing', table: 'isp_accounts', id: customer.account_number, label: `Billing ${r.billing_status ?? 'unknown'}`, is_test: !!r.is_test }];
    return { ok: true, text, citations: cites };
  } catch (err) {
    const authText = operatorAuthFailureText(err);
    if (authText) return { ok: false, text: authText, citations: [] };
    return { ok: false, text: `Could not read billing (${(err as Error).message}).`, citations: [] };
  }
}

/* ── Insights -> get-discovery edge fn (altitude-scoped server-side) ── */
export async function discoveryRead(ctx: PlatformContext): Promise<ActionResult> {
  // Operator altitude reads cross-operator patterns; client altitude reads its
  // own subscriber threads (get-discovery enforces this from the JWT claim).
  const lens = ctx.scope.altitude === 'client' ? 'threads' : 'patterns';
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-discovery`, {
      method: 'POST',
      headers: await authHeaders(ctx),
      body: JSON.stringify({ lens, limit: 6, verticalId: ctx.scope.vertical_id, ...(ctx.scope.client_id ? { clientId: ctx.scope.client_id } : {}) }),
    });
    const data = await res.json().catch(() => ({}));
    // This path already refused to answer a 401 with data, which is why insights
    // was the one action that behaved. It now says WHICH session problem it is:
    // we got here having sent a real operator token, so it is stale, not absent.
    if (isAuthFailureStatus(res.status)) {
      return { ok: false, text: rejectedSessionMessage(), citations: [] };
    }
    if (!res.ok || data?.error) {
      return { ok: false, text: `Insights: ${data?.error || res.status}.`, citations: [] };
    }
    const rows: any[] = Array.isArray(data.rows) ? data.rows : [];
    if (!rows.length) return { ok: true, text: 'No active insights in this scope right now.', citations: [] };
    const cites: AskCitation[] = rows.map((r, i) => ({
      layer: lens === 'patterns' ? 'patterns' : 'threads',
      table: lens === 'patterns' ? 'orbi_system_insights' : 'orbi_customer_insights',
      id: `${lens}-${i}`,
      label: (lens === 'patterns' ? (r.title || r.description) : (r.recurring_issues || r.account_number)) || 'insight',
      is_test: !!r.is_test,
    }));
    const text = lens === 'patterns'
      ? `${rows.length} active patterns. Top: ${(rows[0].title || rows[0].description || 'pattern')} (affecting ${rows[0].affected_count ?? '?'}).`
      : `${rows.length} subscriber memory threads in scope. Most recent: ${(rows[0].recurring_issues || rows[0].account_number || 'thread')}.`;
    return { ok: true, text, citations: cites };
  } catch (err) {
    const authText = operatorAuthFailureText(err);
    if (authText) return { ok: false, text: authText, citations: [] };
    return { ok: false, text: `Could not reach discovery (${(err as Error).message}).`, citations: [] };
  }
}

/* ── Mass resolve -> outage-mass-resolve (JARVIS SITUATIONAL AWARENESS) ──
   The SAME governed endpoint the network page's confirm modal uses: preview
   is a zero-write read of the surgical selection; fire re-computes it
   server-side under the per-(event,account) dedup, test-mode allowlist and
   quiet-hour gates. This client never selects anybody - it only relays. */
export interface MassResolvePreview {
  ok: boolean;
  error?: string;
  test_mode?: boolean;
  test_recipient_count?: number;
  outage_event?: { id?: string; area_label?: string; area_key?: string; down_count?: number; total_in_area?: number };
  included?: Array<{ name?: string; account_number?: string; phone_masked?: string; billing_status?: string }>;
  excluded?: Record<string, any>;
}
export async function massResolvePreview(outageEventId: string): Promise<MassResolvePreview> {
  try {
    const scope: any = (typeof window !== 'undefined' && (window as any).ORA && (window as any).ORA.scope) || {};
    const res = await fetch(`${SUPABASE_URL}/functions/v1/outage-mass-resolve`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ action: 'preview', outage_event_id: outageEventId, verticalId: scope.verticalId || null, clientId: scope.clientId || null }),
    });
    const data = await res.json().catch(() => ({}));
    if (isAuthFailureStatus(res.status)) return { ok: false, error: authRefusalMessage(data, signedInEmail()) };
    if (!res.ok || data?.error) return { ok: false, error: data?.error ? `${data.error}${data.detail ? ` - ${data.detail}` : ''}` : `preview returned ${res.status}` };
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: operatorAuthFailureText(err) ?? (err as Error).message };
  }
}
export async function massResolveFire(outageEventId: string): Promise<{ ok: boolean; error?: string; test_mode?: boolean; calls_placed?: number; calls_attempted?: number; results?: any[]; property_notes?: any[] }> {
  try {
    const scope: any = (typeof window !== 'undefined' && (window as any).ORA && (window as any).ORA.scope) || {};
    const res = await fetch(`${SUPABASE_URL}/functions/v1/outage-mass-resolve`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ action: 'fire', outage_event_id: outageEventId, verticalId: scope.verticalId || null, clientId: scope.clientId || null }),
    });
    const data = await res.json().catch(() => ({}));
    if (isAuthFailureStatus(res.status)) return { ok: false, error: authRefusalMessage(data, signedInEmail()) };
    if (!res.ok || data?.error) return { ok: false, error: data?.error ? `${data.error}${data.detail ? ` - ${data.detail}` : ''}` : `fire returned ${res.status}` };
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: operatorAuthFailureText(err) ?? (err as Error).message };
  }
}

/* ── Ask Orbi to call -> orbi-operator-call (armed, known-subscriber) ── */
export async function requestOrbiCall(
  ctx: PlatformContext,
  customer: CustomerRef | null,
  opts: { dry_run?: boolean; reason?: string } = {}
): Promise<{ ok: boolean; message: string; dialed?: boolean; blocked?: string | null }> {
  if (!customer?.phone && !customer?.account_number) {
    return { ok: false, message: 'Prime a subscriber first. Open one from the list, then ask the AI to call. Outbound is limited to known subscribers.' };
  }
  if (!ctx.scope.org_id) {
    return { ok: false, message: 'No org in scope, so I cannot place a call from here.' };
  }
  // orbi-operator-call requires a phone number in the body (it dispatches to it)
  // even when matching by account_number. The customer list does not carry phone,
  // so resolve it from the subscriber row when it was not primed.
  let phone = customer.phone || '';
  let phoneLookupAuthFailure: string | null = null;
  if (!phone && customer.account_number) {
    try {
      const r = await fetch(
        `${SUPABASE_URL}/rest/v1/isp_accounts?account_number=eq.${encodeURIComponent(customer.account_number)}${clientScopeParam(ctx)}&select=phone&limit=1`,
        { headers: await authHeaders(ctx) }
      );
      throwIfAuthRejected(r.status);
      const rows = await r.json().catch(() => []);
      if (Array.isArray(rows) && rows[0]?.phone) phone = rows[0].phone;
    } catch (err) {
      // An RLS-emptied read looks exactly like "no phone on file". Remember that
      // it was an auth failure so we do not blame the subscriber's record for it.
      phoneLookupAuthFailure = operatorAuthFailureText(err);
    }
  }
  if (phoneLookupAuthFailure) return { ok: false, message: phoneLookupAuthFailure };
  if (!phone) {
    return { ok: false, message: 'This subscriber has no phone number on file, so the AI cannot place a call.' };
  }
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/orbi-operator-call`, {
      method: 'POST',
      headers: await authHeaders(ctx),
      body: JSON.stringify({
        phone,
        account_number: customer.account_number || '',
        org_id: ctx.scope.org_id,
        client_id: ctx.scope.client_id,
        // The operator's stated objective threads through orbi-operator-call ->
        // orbi-outbound-dispatch so Orbi opens the call knowing what to resolve.
        reason: (opts.reason && opts.reason.trim()) ? `operator_from_orb: ${opts.reason.trim()}` : 'operator_initiated_from_orb',
        dry_run: opts.dry_run === true,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (isAuthFailureStatus(res.status)) return { ok: false, message: authRefusalMessage(data, signedInEmail()) };
    if (!res.ok || data?.ok === false) {
      return { ok: false, message: data?.error || `Outbound dispatch returned ${res.status}.` };
    }
    if (data.blocked === 'not_allowlisted') {
      return { ok: true, dialed: false, blocked: 'not_allowlisted', message: 'Held: this number is not on the outbound test allowlist, so I ran a dry run instead of dialing. Add it to ORBI_OUTBOUND_ALLOWLIST to place a real call.' };
    }
    if (data.dry_run) return { ok: true, dialed: false, message: 'Verified the AI can reach this subscriber (dry run, no call placed).' };
    return { ok: true, dialed: true, message: `The AI is calling now${data.is_test ? ' (test subscriber)' : ''}. Callback ${data.callback_id || ''} queued.` };
  } catch (err) {
    const authText = operatorAuthFailureText(err);
    if (authText) return { ok: false, message: authText };
    return { ok: false, message: `Could not reach the outbound service (${(err as Error).message}).` };
  }
}
